import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Authorization } from "../../Context/Authorization";
import Select from "react-select";
import ViewBeatCode from "./ViewBeatCode";

const labelStyle = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: "#4A5568",
  marginBottom: 6,
  letterSpacing: 0.3,
};

const inputStyle = {
  width: "100%",
  padding: "9px 12px",
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  fontSize: 13,
  color: "#2D3748",
  background: "#fff",
  outline: "none",
  transition: "border-color 0.15s",
  boxSizing: "border-box",
};

const selectStyle = {
  ...inputStyle,
  appearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23718096' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 12px center",
  paddingRight: 32,
  cursor: "pointer",
};

const AddBeatCode = () => {
  const [ranges, setRanges]               = useState([]);
  const [selectedRange, setSelectedRange] = useState("");
  const [selectedBlock, setSelectedBlock] = useState("");
  const [selectedVillage, setSelectedVillage] = useState([]);
  const [beatName, setBeatName]           = useState("");
  const [blockList, setBlockList]         = useState([]);
  const [villageList, setVillageList]     = useState([]);
  const [submitting, setSubmitting]       = useState(false);
  const Api = process.env.REACT_APP_API;

  useEffect(() => { fetchRanges(); }, [Api]);

  const fetchRanges = async () => {
    try {
      const res = await axios.get(`${Api}/api/ranges`, Authorization());
      setRanges(res.data);
    } catch {
      toast.error("Failed to fetch ranges");
    }
  };

  const handleRangeChange = (e) => {
    const rangeId = e.target.value;
    setSelectedRange(rangeId);
    setSelectedBlock("");
    setSelectedVillage([]);
    const selected = ranges.find((r) => r._id === rangeId);
    setBlockList(selected?.blocks || []);
    setVillageList([]);
  };

  const handleBlockChange = (e) => {
    const blockCode = e.target.value;
    setSelectedBlock(blockCode);
    setSelectedVillage([]);
    fetchVillages(blockCode);
  };

  const fetchVillages = async (blockCode) => {
    try {
      const selected = ranges.find((r) => r._id === selectedRange);
      const districtCode = selected?.districtCode;
      if (!districtCode) return;
      const res = await axios.get(
        `${Api}/api/lgd/villages?districtCode=${districtCode}&blockCode=${blockCode}`,
        Authorization()
      );
      setVillageList(res.data);
    } catch {
      toast.error("Failed to fetch villages");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const villageNames = villageList
        .filter((v) => selectedVillage.some((sv) => String(sv.value) === String(v.villageCode)))
        .map((v) => v.villageName);
      const villageCodes = selectedVillage.map((v) => Number(v.value));
      const blockObj = blockList.find((b) => String(b.blockCode) === String(selectedBlock));

      await axios.post(`${Api}/api/beat/create`, {
        rangeId: selectedRange,
        beatName,
        villageName: villageNames,
        villageCode: villageCodes,
        blockName: [blockObj?.blockName],
        blockCode: [Number(blockObj?.blockCode)],
      }, Authorization());

      toast.success("Beat created successfully");
      setSelectedRange("");
      setSelectedBlock("");
      setSelectedVillage([]);
      setBeatName("");
      setBlockList([]);
      setVillageList([]);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error creating Beat");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedRangeName = ranges.find((r) => r._id === selectedRange)?.rangeName || "";

  return (
    <div style={{ padding: "0 0 32px" }}>

      {/* ── Page Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: "linear-gradient(135deg,#1B5E20,#2E7D32)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <i className="mdi mdi-map-marker-path" style={{ color: "#fff", fontSize: 20 }} />
          </div>
          <div>
            <h4 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1A202C" }}>Beat Management</h4>
            <p style={{ margin: 0, fontSize: 12, color: "#718096" }}>
              <span style={{ color: "#2E7D32", cursor: "pointer" }}>VFC</span>
              {" / "}
              <span style={{ color: "#718096" }}>Range</span>
              {" / "}
              <span style={{ color: "#2D3748", fontWeight: 600 }}>Beat</span>
            </p>
          </div>
        </div>
      </div>

      {/* ── Create Beat Form Card ── */}
      <div style={{
        background: "#fff",
        borderRadius: 14,
        border: "1px solid #E2E8F0",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        overflow: "hidden",
        marginBottom: 24,
      }}>
        {/* Card header */}
        <div style={{
          padding: "14px 20px",
          borderBottom: "1px solid #E2E8F0",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <i className="mdi mdi-plus-circle-outline" style={{ color: "#2E7D32", fontSize: 18 }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748" }}>Create Beat</div>
            <div style={{ fontSize: 11, color: "#A0AEC0" }}>Fill in the details below to register a new beat</div>
          </div>
        </div>

        <div style={{ padding: 24 }}>
          <form onSubmit={handleSubmit}>
            {/* Row 1: Range + Beat Name */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
              <div>
                <label style={labelStyle}>Range <span style={{ color: "#E53E3E" }}>*</span></label>
                <select
                  value={selectedRange}
                  onChange={handleRangeChange}
                  required
                  style={selectStyle}
                  onFocus={(e) => e.target.style.borderColor = "#2E7D32"}
                  onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}
                >
                  <option value="">Select Range</option>
                  {ranges
                    .sort((a, b) => a.rangeName.localeCompare(b.rangeName))
                    .map((r) => (
                      <option key={r._id} value={r._id}>{r.rangeName}</option>
                    ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Beat Name <span style={{ color: "#E53E3E" }}>*</span></label>
                <input
                  type="text"
                  value={beatName}
                  onChange={(e) => setBeatName(e.target.value)}
                  placeholder="Enter beat name"
                  required
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = "#2E7D32"}
                  onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}
                />
              </div>
            </div>

            {/* Row 2: Block + Villages */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
              <div>
                <label style={labelStyle}>Block <span style={{ color: "#E53E3E" }}>*</span></label>
                <select
                  value={selectedBlock}
                  onChange={handleBlockChange}
                  required
                  disabled={!blockList.length}
                  style={{ ...selectStyle, background: !blockList.length ? "#F7FAFC" : "#fff", color: !blockList.length ? "#A0AEC0" : "#2D3748" }}
                  onFocus={(e) => e.target.style.borderColor = "#2E7D32"}
                  onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}
                >
                  <option value="">{blockList.length ? "Select Block" : "Select Range first"}</option>
                  {blockList.map((b) => (
                    <option key={b.blockCode} value={b.blockCode}>{b.blockName}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Villages</label>
                <Select
                  isMulti
                  isDisabled={!villageList.length}
                  options={villageList.map((v) => ({ value: v.villageCode, label: v.villageName }))}
                  value={selectedVillage}
                  onChange={(opts) => setSelectedVillage(opts || [])}
                  placeholder={villageList.length ? "Select Village(s)" : "Select Block first"}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderRadius: 8,
                      borderColor: state.isFocused ? "#2E7D32" : "#E2E8F0",
                      boxShadow: state.isFocused ? "0 0 0 3px rgba(46,125,50,0.1)" : "none",
                      fontSize: 13,
                      minHeight: 38,
                      "&:hover": { borderColor: "#2E7D32" },
                    }),
                    multiValue: (base) => ({ ...base, background: "#E8F5E9", borderRadius: 6 }),
                    multiValueLabel: (base) => ({ ...base, color: "#1B5E20", fontSize: 12, fontWeight: 600 }),
                    multiValueRemove: (base) => ({ ...base, color: "#2E7D32", "&:hover": { background: "#C8E6C9", color: "#1B5E20" } }),
                    menu: (base) => ({ ...base, borderRadius: 10, border: "1px solid #E2E8F0", boxShadow: "0 8px 24px rgba(0,0,0,0.1)", zIndex: 10 }),
                    option: (base, state) => ({
                      ...base,
                      fontSize: 13,
                      background: state.isSelected ? "#E8F5E9" : state.isFocused ? "#F7FAFC" : "#fff",
                      color: state.isSelected ? "#1B5E20" : "#2D3748",
                    }),
                  }}
                />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "10px 22px",
                background: submitting ? "#A5D6A7" : "linear-gradient(135deg,#1B5E20,#2E7D32)",
                color: "#fff", border: "none", borderRadius: 9,
                fontSize: 13, fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer",
                boxShadow: "0 2px 8px rgba(46,125,50,0.35)",
                transition: "opacity 0.15s",
              }}
            >
              {submitting
                ? <><i className="mdi mdi-loading mdi-spin" style={{ fontSize: 16 }} /> Creating…</>
                : <><i className="mdi mdi-map-marker-plus" style={{ fontSize: 16 }} /> Create Beat</>
              }
            </button>
          </form>
        </div>
      </div>

      {/* ── View Beats ── */}
      <ViewBeatCode rangeId={selectedRange} rangeName={selectedRangeName} />
    </div>
  );
};

export default AddBeatCode;
